// Bundle picker (BUNDLE-01 / HERO-02 behavior slice): <bundle-picker> web component.
//
// ISOLATION / INTEGRATION CONTRACT
// ================================
// This component turns the server-rendered radio cards (blocks/bundle-picker.liquid +
// snippets/bundle-card.liquid, 1 card "Nur Sonodrum" + N set cards from the
// custom.bundle_produkte list metafield) into a real in-page product swap by feeding
// Horizon's OWN combined-listing event pipeline — it NEVER edits shared theme files:
//
//   1. dispatch ProductSelectEvent (pending promise) -> product-form arms its ATC queue
//   2. fetch the target product's full page     -> ALWAYS the same template (#viewParam:
//      ?view= from the URL, else this page's template suffix — the block ids that
//      product-price/variant-picker match on are template-scoped), color
//      carried over via ?option_values= (D-03: mapped by option-value NAME — IDs are
//      product-specific and never reused across products)
//   3. strip <media-gallery> from set-product Documents (D-06: the gallery NEVER
//      shows set photos — it always shows the single drum). Color changes while a
//      set is active re-sync the gallery to the DRUM's image of the picked color
//      (D-AMEND-6): fetch the base PDP with the color mapped by NAME via the base
//      card's data-option-map, then replaceWith its media-gallery — the identical
//      mechanism media-gallery.js itself uses on variant:update.
//   4. targeted own morphs: variant-picker (via its public updateVariantPicker API,
//      the same call quick-add.js/product-card.js use) + the plain-Liquid
//      product-savings block; pre-set sticky-add-to-cart's dataset.productId
//      (its guard is NOT newProduct-aware)
//   5. resolve that promise with detail {html, resource, newProduct {id, url}} -> product-form,
//      product-price, product-inventory, sticky-add-to-cart, local-pickup update
//      themselves from data.html
//   6. the URL is NEVER rewritten (D-AMEND-5): the address bar keeps the single
//      drum's PDP URL through any number of swaps/color changes — reload/share
//      always lands on the base PDP with the picker in its default state. The
//      native variant-picker DOES write ?variant= on color changes while a set
//      product is active (variant-picker.js is shared and stays untouched), so
//      the capture guard scrubs that set-variant param right back off the URL.
//
// STATEFULNESS (Pattern 6): the bundle-picker itself is NEVER morphed or replaced
// from fetched HTML (the set products carry no back-reference metafield in v1, so
// fetched pages render this block empty). Cards + active state live client-side
// from the initial render; only input.checked is toggled.
//
// ERROR PATH (Pitfall 3): product-form releases #variantChangeInProgress only when
// the ProductSelectEvent promise settles. Every non-abort failure therefore reverts
// the radio selection AND resolves the pending promise with a restore payload —
// otherwise every ATC click after a failed fetch would be silently queued forever.
//
// SPAM-CLICK GUARD (Pitfall 8): product-form's own ATC queue captures the
// *intended* variant id at click time (URL ?variant= || input[name=id]) — during
// a cross-product swap both still point at the PREVIOUS product, so its native
// queue would flush spam clicks with the OLD product's variant. While a swap is
// in flight, a capture-phase submit interceptor on the section therefore holds
// ATC submits back and replays them once the swap has committed (new id) or
// reverted (previous id) — the cart only ever receives what the cards display.
//
// A capture-phase listener on the section keeps D-06 enforced for COLOR changes made
// via the native variant-picker while a set product is active (capture on the section
// fires before media-gallery's bubble-phase listener on the same node) — and, since
// stripping alone would freeze the gallery, follows up with the D-AMEND-6 base-color
// gallery re-sync described above.

import { Component } from '@theme/component';
import { ProductSelectEvent, StandardEvents } from '@shopify/events';
import { morph } from '@theme/morph';
import { getViewParameterValue, yieldToMainThread } from '@theme/utilities';

// HORIZON 4.1.5 (Phase 17): the 3.4.0 pair VariantSelectedEvent ("variant:selected",
// arms product-form) + VariantUpdateEvent ("variant:update", carries html/resource) is
// gone from @theme/events. 4.1.5 uses ONE standard event, ProductSelectEvent
// ("shopify:product:select" from @shopify/events), dispatched BEFORE the fetch with a
// deferred `promise`; listeners (product-form, product-price, media-gallery,
// sticky-add-to-cart, product-inventory, local-pickup) `await event.promise` and read
// `{ variant, detail: { html, productId, newProduct, sourceId, resource } }` — the same
// payload the old VariantUpdateEvent carried. The steps below map 1:1:
//   step 2 "arm"    = dispatch ProductSelectEvent with a pending deferred
//   step 6 "commit" = resolve that deferred with the fetched Document
//   error/restore   = resolve it with the restore Document (never leave it pending —
//                     product-form's #variantChangeInProgress is released in its finally)
// Superseding flows (fast double-toggle) REUSE the pending deferred instead of
// dispatching again: one event, one promise, settled once by the flow that commits or
// restores. Rejecting it (variant-picker's own abort path) would surface as an
// "Uncaught (in promise) AbortError" in every listener — we never reject.

class BundlePicker extends Component {
  /** @type {AbortController} lifecycle controller — every listener registers with its
   * signal; RECREATED on every connect (WR-03: a reconnected element would otherwise
   * register nothing on an already-aborted signal yet still re-enable the radios). */
  #listeners = new AbortController();

  /** @type {AbortController | undefined} in-flight fetch controller (fast double-toggle abort) */
  #fetchController;

  /** @type {AbortController | undefined} in-flight D-AMEND-6 gallery re-sync (latest color wins) */
  #gallerySyncController;

  /** @type {string} product id of the initially rendered page (the single drum) */
  #originalProductId = '';

  /** @type {string} product id the buy-box is currently swapped to */
  #activeProductId = '';

  /** @type {HTMLInputElement | undefined} radio of the last COMMITTED (successful) card */
  #activeCard;

  /** @type {object | null} latest known variant resource — restore payload for the error path */
  #lastVariant = null;

  /** @type {AbortController | undefined} capture-phase ATC submit interceptor (spam-click guard, Pitfall 8) */
  #atcInterceptor;

  /** @type {HTMLFormElement[]} ATC submits intercepted during an in-flight swap — replayed after commit/revert */
  #atcReplayQueue = [];

  /** @type {{ promise: Promise<any>, resolve: (v: any) => void, reject: (e: any) => void } | undefined}
   * deferred of the ProductSelectEvent currently in flight (4.1.5 pipeline) — settled exactly once */
  #pendingSelect;

  connectedCallback() {
    super.connectedCallback();
    // WR-03: disconnect aborts #listeners — a re-parented/reconnected element
    // needs a fresh controller or every addEventListener below is a no-op.
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.#originalProductId = this.dataset.originalProductId ?? '';
    this.#activeProductId = this.#originalProductId;

    // Progressive enhancement: the 03-02 markup ships the radios `disabled` so the
    // display can never drift from the ATC form pre-JS. JS is here — unlock them.
    const radios = this.#radios;
    for (const radio of radios) radio.removeAttribute('disabled');
    this.#activeCard = radios.find((radio) => radio.checked) ?? radios[0];
    this.#syncSelectionState();

    // Cache the currently selected variant for the error-restore path.
    this.#lastVariant = this.#parseVariantJson(document);

    // Native radiogroup provides arrow-key/space handling — only `change` is needed.
    this.addEventListener('change', this.#onChange, { signal });

    // D-06 capture guard: strips media-gallery from ANY product-select html (e.g.
    // variant-picker color changes) while a set product is active. Doubles as the
    // cache for the latest variant resource. Capture on the section fires before the
    // bubble-phase listeners on the same node (media-gallery, product-form) — so our
    // `.then` on event.promise is registered FIRST and runs first (promise reactions
    // fire in registration order): the gallery is gone from `html` before
    // media-gallery.js reads it.
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#guardProductSelect, {
      capture: true,
      signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners.abort();
    this.#fetchController?.abort();
    this.#gallerySyncController?.abort();
    this.#atcInterceptor?.abort();
    this.#atcReplayQueue.length = 0;
    // Never leave listeners awaiting a promise nobody will settle (4.1.5 pipeline).
    if (this.#pendingSelect) {
      const deferred = this.#pendingSelect;
      this.#pendingSelect = undefined;
      deferred.resolve({ variant: null, detail: { html: null, productId: this.#activeProductId, sourceId: this.id, resource: null } });
    }
  }

  /**
   * Arms the spam-click guard (Pitfall 8): while a swap is in flight, ATC submits
   * are stopped and queued for replay. The listener sits on WINDOW in capture
   * phase because Horizon's `on:submit` delegation is a document-level capture
   * listener (component.js registerEventListeners) — window capture is the only
   * spot in the propagation path that fires before it. Scoped to the product
   * form of this block's own section. Stays armed across fast double-toggles —
   * the flow that finally commits (or reverts) replays the queue.
   */
  #armAtcInterceptor() {
    if (this.#atcInterceptor) return;
    const section = this.closest('.shopify-section');
    if (!section) return;
    this.#atcInterceptor = new AbortController();
    window.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (!form.closest('product-form-component') || form.closest('.shopify-section') !== section) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.#atcReplayQueue.push(form);
      },
      { capture: true, signal: this.#atcInterceptor.signal }
    );
  }

  /**
   * Disarms the spam-click guard and replays the held ATC submits. Called AFTER
   * the VariantUpdateEvent of the committing (or restoring) flow, so
   * input[name=id] already carries the id that matches what the cards display.
   */
  async #disarmAtcInterceptorAndReplay() {
    this.#atcInterceptor?.abort();
    this.#atcInterceptor = undefined;
    const queue = this.#atcReplayQueue.splice(0);
    for (const form of queue) {
      form.requestSubmit();
      await yieldToMainThread();
    }
  }

  /** @returns {HTMLInputElement[]} the card radios (1 + N sets, any N) */
  get #radios() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input[name="bundle-option"]')]);
  }

  /**
   * Capture-phase product-select guard (D-06) + latest-variant cache.
   * While a set is active it also (a) scrubs the native picker's ?variant=
   * URL write (D-AMEND-5) and (b) re-syncs the gallery to the DRUM's image of
   * the newly picked color (D-AMEND-6) — but only for events that did NOT
   * originate from this component (event.target !== this): our own swap/restore
   * dispatches carry the color over unchanged, so the gallery is already right.
   * The set-active decision is taken synchronously AT DISPATCH (like the old
   * variant:update guard did): a toggle back to the base card that lands while a
   * native color fetch is still pending must not let that fetch's SET gallery
   * through afterwards.
   * @param {ProductSelectEvent} event
   */
  #guardProductSelect = (event) => {
    const setActive = this.#activeProductId !== this.#originalProductId;

    if (setActive) {
      this.#scrubVariantParamFromUrl();
      if (event.target !== this) void this.#syncGalleryToBaseColor();
    }

    const promise = /** @type {any} */ (event).promise;
    if (!promise || typeof promise.then !== 'function') return;
    promise
      .then((/** @type {any} */ result) => {
        const detail = result?.detail;
        if (detail?.resource) this.#lastVariant = detail.resource;
        if (setActive) {
          detail?.html?.querySelector?.('media-gallery')?.remove();
          // 4.1.5 variant-picker schedules its ?variant= write (yieldToMainThread)
          // AFTER dispatching — i.e. after the scrub scheduled above already ran
          // (measured 2026-09-11: ?variant= stayed on the release theme, not on
          // live). Scrub once more once the fetch has settled: by then the
          // picker's write is long done.
          this.#scrubVariantParamFromUrl();
        }
      })
      .catch(() => {
        /* native variant-picker rejects its deferred on abort — nothing to guard then */
      });
  };

  /**
   * Dispatches the 4.1.5 ProductSelectEvent for a swap to `productId` with a fresh
   * deferred and remembers it as the pending one. Listeners in this section
   * (product-form, product-price, media-gallery, sticky-add-to-cart, …) start
   * awaiting `event.promise` right away — the caller MUST settle it via
   * #resolvePendingSelect (commit or restore), never leave it pending.
   * @param {string} productId
   */
  #dispatchSelect(productId) {
    const deferred = ProductSelectEvent.createPromise();
    this.#pendingSelect = deferred;
    this.dispatchEvent(
      new ProductSelectEvent({
        product: { id: productId, title: '', handle: '' },
        selectedOptions: [],
        detail: {},
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  /**
   * Settles the pending ProductSelectEvent with a VariantUpdateEvent-shaped payload.
   * `html: null` is the "release without touching anything" payload: every 4.1.5
   * listener early-returns on missing html, product-form still clears its
   * in-progress flag in `finally` and drains its ATC queue.
   * @param {{ html: Document | null, productId: string, newProduct: { id: string, url: string }, resource: object | null, variant?: object | null }} payload
   */
  #resolvePendingSelect({ html, productId, newProduct, resource, variant = null }) {
    const deferred = this.#pendingSelect ?? this.#dispatchSelect(productId);
    this.#pendingSelect = undefined;
    deferred.resolve({
      variant,
      detail: { html, productId, newProduct, sourceId: this.id, resource },
    });
  }

  /**
   * D-AMEND-5 enforcement against the NATIVE picker: variant-picker.js (shared,
   * untouched) replaceStates ?variant=<id> on every color change — while a set
   * product is active that id is a SET variant and must never reach the address
   * bar. Scheduled via yieldToMainThread so it runs after the picker's own
   * scheduled write; pathname is never touched by the picker here (no combined
   * listing), so deleting ?variant= restores the clean base PDP URL.
   */
  #scrubVariantParamFromUrl() {
    yieldToMainThread().then(() => {
      if (this.#activeProductId === this.#originalProductId) return;
      const url = new URL(window.location.href);
      if (!url.searchParams.has('variant')) return;
      url.searchParams.delete('variant');
      history.replaceState({}, '', url.toString());
    });
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const card = event.target;
    if (!(card instanceof HTMLInputElement) || card.name !== 'bundle-option') return;
    if (card === this.#activeCard) {
      // CR-01: toggle BACK to the committed card while a swap is in flight —
      // ignoring this would let the late fetch commit underneath the visible
      // selection (UI shows card A, buy-box carries product B). Abort the
      // in-flight flow and restore the committed state instead.
      if (this.#fetchController) {
        this.#fetchController.abort();
        this.#fetchController = undefined; // aborted flow no longer owns the busy state
        this.querySelector('[role="radiogroup"]')?.removeAttribute('aria-busy');
        this.#restore(card); // re-checks card, dispatches the restoring VariantUpdateEvent, disarms/replays the ATC guard
      }
      return;
    }
    this.#switchTo(card);
  };

  /**
   * Swaps the buy-box to the product of the given card (any of the N cards).
   * @param {HTMLInputElement} card - The card radio that was just checked.
   */
  async #switchTo(card) {
    const previousCard = this.#activeCard;
    const radiogroup = this.querySelector('[role="radiogroup"]');
    const targetUrl = (card.dataset.productUrl ?? '').split('?')[0];

    // WR-02 defense-in-depth: a card without product data (Liquid filters these
    // out, but belt-and-braces) would fetch the CURRENT page and commit with an
    // empty product id — refuse it before anything is armed.
    if (!targetUrl || !card.dataset.productId) {
      console.warn('[bundle-picker] card without product data ignored');
      if (previousCard) previousCard.checked = true;
      return;
    }

    // 1. Color sync (D-03): current option-value NAMES -> target product's ids.
    const optionValueIds = this.#mapSelectedOptionValues(card);

    // 2. Arm product-form's ATC queue BEFORE fetching (native dispatch order) +
    //    the capture-phase spam-click guard (Pitfall 8): product-form's own queue
    //    would flush with the OLD product's variant id. 4.1.5: ONE ProductSelectEvent
    //    with a pending deferred; a superseding flow reuses the pending one (settled
    //    once, by whichever flow commits or restores).
    if (!this.#pendingSelect) this.#dispatchSelect(card.dataset.productId);
    this.#armAtcInterceptor();

    // 3. Busy state + abortable fetch (fast double-toggle aborts the previous one).
    radiogroup?.setAttribute('aria-busy', 'true');
    this.#fetchController?.abort();
    const controller = new AbortController();
    this.#fetchController = controller;

    const params = new URLSearchParams();
    params.set('view', this.#viewParam());
    if (optionValueIds) params.set('option_values', optionValueIds.join(','));

    try {
      const response = await fetch(`${targetUrl}?${params}`, { signal: controller.signal });
      if (!response.ok) throw new Error(`[bundle-picker] fetch failed: HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');

      // 4. Prepare the fetched Document. D-06: the gallery never swaps to SET
      //    photos — strip it from set-product documents only. Toggling back to
      //    the base card keeps the fetched BASE gallery in the payload, so the
      //    native media-gallery handler restores it color-correct (D-AMEND-6).
      html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
      if (card.dataset.productId !== this.#originalProductId) {
        html.querySelector('media-gallery')?.remove();
      }

      const variantJsonText = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
      if (!variantJsonText) throw new Error('[bundle-picker] no variant JSON in fetched document');
      const variantJson = JSON.parse(variantJsonText);

      // CR-01 hardening: an abort (toggle-back or newer selection) makes the
      // commit path impossible even if the fetch already resolved — everything
      // below mutates live state and must only run for the owning flow.
      if (controller.signal.aborted) {
        throw new DOMException('bundle swap aborted after fetch resolved', 'AbortError');
      }

      // 5. Targeted own morphs (only the pieces that are NOT newProduct-aware).
      //    variant-picker: use its own public update API (quick-add.js precedent) —
      //    keyed morph + dataset.productId/productUrl re-point in one tested call.
      const currentPicker = this.closest('.shopify-section')?.querySelector('variant-picker');
      const fetchedPicker = html.querySelector('variant-picker');
      if (currentPicker && fetchedPicker) {
        if (typeof (/** @type {any} */ (currentPicker).updateVariantPicker) === 'function') {
          /** @type {any} */ (currentPicker).updateVariantPicker(html);
        } else {
          morph(currentPicker, fetchedPicker);
        }
      }

      // sticky-add-to-cart's variant:update guard compares data.productId against its
      // own dataset.productId and is NOT newProduct-aware — pre-set it (Pattern 3).
      const sticky = document.querySelector('sticky-add-to-cart');
      if (sticky instanceof HTMLElement) sticky.dataset.productId = card.dataset.productId ?? '';

      // Price cluster (savings flag, urgency line, product-savings block): plain
      // Liquid siblings that are not newProduct-aware — morph them from the fetched page.
      this.#morphPriceCluster(html);

      // Commit client state BEFORE dispatching so the capture guard sees the new
      // active product while our own event passes through it. The picker itself is
      // never morphed (Pattern 6) — the radio's checked state IS the card state.
      this.#activeProductId = card.dataset.productId ?? '';
      this.#activeCard = card;
      this.#lastVariant = variantJson;
      this.#syncSelectionState();

      // 6. Feed the pipeline: product-form (input[name=id] + ATC queue flush),
      //    product-price, product-inventory, sticky, local-pickup do the rest.
      //    `variant` mirrors variant-picker.js: analytics-shaped selectedVariant
      //    from the fetched page's view-event-payload (null if absent).
      const viewPayload = html
        .querySelector('variant-picker script[type="application/json"]')
        ?.closest('[view-event-payload]')
        ?.getAttribute('view-event-payload')
        ?.trim();
      let variant = null;
      try {
        variant = (viewPayload && JSON.parse(viewPayload))?.product?.selectedVariant ?? null;
      } catch {
        variant = null;
      }
      this.#resolvePendingSelect({
        html,
        productId: card.dataset.productId ?? '',
        newProduct: { id: card.dataset.productId ?? '', url: targetUrl },
        resource: variantJson,
        variant,
      });

      // 7. NO URL rewrite (D-AMEND-5): the address bar keeps the base PDP URL.
      //    Every fetch in this component uses the cards' data-product-url, never
      //    window.location — so nothing downstream depends on a rewritten URL.

      // 8. Swap committed — release the spam-click guard: input[name=id] now
      //    carries the NEW variant, held ATC submits replay against it.
      await this.#disarmAtcInterceptorAndReplay();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        // A newer #switchTo or a CR-01 toggle-back owns the flow now — that
        // path dispatches its own state-releasing event; nothing to do here.
        console.warn('[bundle-picker] swap aborted (superseded or toggled back)');
        return;
      }
      console.error(error);
      this.#restore(previousCard); // Pitfall 3: reverts the card + dispatches the restore VariantUpdateEvent
    } finally {
      // Only the flow that still owns the controller may clear the busy state.
      if (this.#fetchController === controller) radiogroup?.removeAttribute('aria-busy');
    }
  }

  /**
   * Error path (Pitfall 3): reverts the radio selection to the last committed card
   * and dispatches a restoring VariantUpdateEvent built from the LIVE (unchanged)
   * DOM so product-form releases its queue against the previous, still-valid state.
   * @param {HTMLInputElement | undefined} previousCard
   */
  #restore(previousCard) {
    if (!previousCard) return;

    previousCard.checked = true; // radio group semantics uncheck the failed card

    const prevId = previousCard.dataset.productId ?? '';
    const prevUrl = (previousCard.dataset.productUrl ?? '').split('?')[0];

    // Detached clone of the current section = morph-safe "no-op" html payload
    // (morphing live elements against themselves is not safe — clone instead).
    const restoreDoc = document.implementation.createHTMLDocument('');
    const section = this.closest('.shopify-section');
    if (section) restoreDoc.body.appendChild(restoreDoc.importNode(section, true));
    restoreDoc.querySelector('media-gallery')?.remove(); // keep the gallery handler a no-op

    const sticky = document.querySelector('sticky-add-to-cart');
    if (sticky instanceof HTMLElement) sticky.dataset.productId = prevId;

    this.#activeProductId = prevId;
    this.#activeCard = previousCard;
    this.#syncSelectionState();

    // WR-01: never dispatch a null resource — product-form would blank
    // input[name=id] and DISABLE the ATC button permanently. If the variant
    // cache is empty (no variant JSON on the page, no prior variant:update),
    // synthesize a minimal resource from the live form's current variant id;
    // if even that is unavailable, skip the dispatch (radio revert +
    // interceptor replay below still run — the form state was never changed).
    const fallbackId = this.closest('.shopify-section')?.querySelector('form input[name="id"]')?.value;
    const resource = this.#lastVariant ?? (fallbackId ? { id: Number(fallbackId), available: true } : null);

    // 4.1.5: the pending deferred MUST be settled either way. With a resource the
    // restore Document re-syncs product-form/price/sticky to the previous state;
    // without one, `html: null` merely releases product-form's in-progress flag
    // (WR-01: never hand a null resource to product-form).
    this.#resolvePendingSelect({
      html: resource ? restoreDoc : null,
      productId: prevId,
      newProduct: { id: prevId, url: prevUrl },
      resource,
    });

    // Release the spam-click guard on the error path too: the UI has reverted,
    // so held submits replay against the previous (still displayed) variant.
    void this.#disarmAtcInterceptorAndReplay();
  }

  /**
   * D-AMEND-6: while a set product is active, the gallery must follow color
   * changes with the SINGLE DRUM's image of the picked color (never set photos).
   * Maps the currently checked option-value NAMES to the BASE product's option
   * value ids via the base card's own data-option-map (identical option values
   * guaranteed, D-03), fetches the base PDP, and replaceWith()s its
   * media-gallery — the exact operation media-gallery.js performs natively on
   * variant:update. Latest-wins: a newer color click aborts the previous sync.
   * Failures keep the current (still base-product) gallery — freeze over wrong
   * images.
   */
  async #syncGalleryToBaseColor() {
    const section = this.closest('.shopify-section');
    if (!section || !section.querySelector('media-gallery')) return;

    const baseCard = this.#radios.find((radio) => radio.dataset.productId === this.#originalProductId);
    const baseUrl = (baseCard?.dataset.productUrl || this.dataset.originalProductUrl || '').split('?')[0];
    if (!baseCard || !baseUrl) return;

    // Map the picked color by NAME onto the base product; if the mapping fails,
    // fetching without option_values would render the FIRST variant's gallery
    // (potentially the wrong color) — skip instead.
    const optionValueIds = this.#mapSelectedOptionValues(baseCard);
    if (!optionValueIds) return;

    this.#gallerySyncController?.abort();
    const controller = new AbortController();
    this.#gallerySyncController = controller;

    try {
      const params = new URLSearchParams();
      params.set('view', this.#viewParam());
      params.set('option_values', optionValueIds.join(','));

      const response = await fetch(`${baseUrl}?${params}`, { signal: controller.signal });
      if (!response.ok) throw new Error(`[bundle-picker] gallery sync fetch failed: HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');

      const fetchedGallery = html.querySelector('media-gallery');
      // Re-query at apply time — the live gallery may have been replaced by the
      // native pipeline in the meantime (e.g. a toggle back to the base card).
      const liveGallery = this.closest('.shopify-section')?.querySelector('media-gallery');
      if (!fetchedGallery || !liveGallery || controller.signal.aborted) return;

      liveGallery.replaceWith(fetchedGallery);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return; // newer color click owns the sync
      console.warn('[bundle-picker] gallery color sync failed — keeping current gallery', error);
    }
  }

  /**
   * The `view` param every fetch in this component MUST carry so the target
   * product renders in the SAME template as the live page.
   *
   * Why this is not optional (Fix 2026-09-04, reported by Johannes): Horizon
   * renders `block.id` as "<template hash>__<block key>" and the hash belongs to
   * the TEMPLATE, not to the product — measured live, base product and set
   * product return byte-identical `data-block-id` values under the same ?view=,
   * and different ones without it. `product-price.js` looks the fetched document
   * up by exactly `product-price[data-block-id="…"]` and returns silently when it
   * misses, so the buy-box price would keep showing the BASE price after a set
   * was picked. Same coupling for variant-picker's keyed morph.
   *
   * Precedence: an explicit ?view= in the URL wins (that is how the redesign
   * template is previewed); otherwise the suffix of the template that rendered
   * this block. An empty string is deliberately still sent — Shopify answers
   * "?view=" with the DEFAULT template, which is exactly right when the live
   * page has no suffix.
   *
   * @returns {string} value for the `view` query param
   */
  #viewParam() {
    return getViewParameterValue() ?? this.dataset.templateSuffix ?? '';
  }

  /**
   * D-03 color carry-over: reads the currently checked option-value NAMES from the
   * live variant-picker and maps them to the target card's option-value ids via the
   * server-rendered data-option-map JSON (keys are byte-identical to value.name).
   *
   * Fallback contract (plan/UI-SPEC): if any selected name is missing from the
   * target map or marked unavailable, return null -> fetch WITHOUT option_values
   * (the server falls back to selected_or_first_available_variant and renders the
   * grey-out/sold-out states itself).
   *
   * @param {HTMLInputElement} card - The target card radio.
   * @returns {string[] | null} option value ids for ?option_values=, or null.
   */
  #mapSelectedOptionValues(card) {
    try {
      const mapScript = card.closest('.bundle-card')?.querySelector('script[data-option-map]');
      const picker = this.closest('.shopify-section')?.querySelector('variant-picker');
      if (!mapScript || !picker) return null;

      /** @type {Record<string, Record<string, {id: number | string, available: boolean}>>} */
      const optionMap = JSON.parse(mapScript.textContent ?? '');
      const groups = Object.values(optionMap);

      // Same selector set the native picker uses for its own selected values.
      const checked = [...picker.querySelectorAll('select option[selected], fieldset input:checked')];
      if (checked.length === 0) return null;

      const ids = [];
      for (const [index, element] of checked.entries()) {
        const name = /** @type {HTMLInputElement | HTMLOptionElement} */ (element).value;

        // Options render in options_with_values order on BOTH products (operator
        // contract: identical option sets) -> pair by index; if the shapes differ,
        // fall back to searching every group for the name.
        let entry = groups.length === checked.length ? groups[index]?.[name] : undefined;
        if (!entry) entry = groups.map((group) => group?.[name]).find((candidate) => candidate);

        if (!entry || entry.available === false || entry.id == null) return null;
        ids.push(String(entry.id));
      }
      return ids;
    } catch (error) {
      console.warn('[bundle-picker] option map lookup failed — fetching without option_values', error);
      return null;
    }
  }

  /**
   * CRO amendment 2026-09-03 (Johannes): mirrors the COMMITTED selection onto
   * data-selection so the block's own CSS can hide the buy-box price + savings
   * blocks below while the baseline card is selected — that card already shows
   * compare-at price AND price, so the blocks below would repeat exactly those
   * two numbers. A bundle card shows only the surcharge, so there the blocks
   * below carry new information (base price + surcharge = total) and stay.
   *
   * Written at COMMIT time (not on click) so the visible state never runs ahead
   * of the price the buy-box actually shows while a swap is in flight. The
   * picker element itself is never morphed (Pattern 6), so the attribute
   * survives every morph in the pipeline.
   */
  #syncSelectionState() {
    const activeId = this.#activeCard?.dataset.productId || this.#activeProductId;
    this.dataset.selection = activeId === this.#originalProductId ? 'baseline' : 'bundle';
  }

  /**
   * Morphs the whole PRICE CLUSTER — every DOM sibling that follows the picker —
   * from the fetched target-product document.
   *
   * Why "everything after the picker" and not a fixed selector list: the cluster
   * is template-specific. Redesign: divider + product-price + product-savings.
   * Sonodrum-Premium: divider + a custom-liquid "Du sparst bis zu X" flag +
   * product-price + a custom-liquid "Angebot verlaengert bis …" line. Those
   * custom-liquid blocks render as ANONYMOUS <div>s (blocks/custom-liquid.liquid
   * emits a bare div, no class, no shopify_attributes), so there is nothing to
   * select them by — but they read `product.*` and would otherwise keep showing
   * the BASE drum's discount while a set is active (measured 2026-09-04:
   * "Du sparst bis zu 170,00 €" next to the set price of 269,95 €).
   *
   * Pairing is anchored on product-price, not on the picker: the picker renders
   * EMPTY on set-product pages (Pattern 6 — set products carry no back-reference
   * metafield), so its element is missing in the fetched document and index
   * pairing from the top would be off by one. Both documents render the same
   * template (#viewParam), so the offset relative to product-price is identical.
   * product-price itself is skipped — it updates itself from the same event.
   *
   * Requires the picker to sit DIRECTLY above the price cluster in the block
   * order; the same requirement the baseline-hiding CSS below has.
   *
   * @param {Document} html - The fetched target-product document.
   */
  #morphPriceCluster(html) {
    const section = this.closest('.shopify-section');
    const price = section?.querySelector('product-price');
    const blockId = price instanceof HTMLElement ? price.dataset.blockId : undefined;
    if (!price || !blockId) return;

    const fetchedPrice = html.querySelector(`product-price[data-block-id="${blockId}"]`);
    if (!fetchedPrice?.parentElement || !price.parentElement) return;

    const live = [...price.parentElement.children];
    const fetched = [...fetchedPrice.parentElement.children];
    const liveAnchor = live.indexOf(price);
    const fetchedAnchor = fetched.indexOf(fetchedPrice);

    for (let i = live.indexOf(this) + 1; i < live.length; i++) {
      const current = live[i];
      const next = fetched[fetchedAnchor + (i - liveAnchor)];
      // Skip the price (owns its own update) and bail out of any pair whose
      // shape does not line up — a stale morph is worse than no morph.
      if (!current || !next || current === price || current.tagName !== next.tagName) continue;
      morph(current, next);
    }
  }

  /**
   * Parses the selected variant JSON the variant-picker embeds in its markup.
   * @param {Document} root
   * @returns {object | null}
   */
  #parseVariantJson(root) {
    try {
      const text = root.querySelector('variant-picker script[type="application/json"]')?.textContent;
      return text ? JSON.parse(text) : null;
    } catch {
      return null;
    }
  }
}

if (!customElements.get('bundle-picker')) {
  customElements.define('bundle-picker', BundlePicker);
}
